import * as z from 'zod';

export const GetEmbeddableItemRequest = z.object({
  embeddableItemId: z
    .string()
    .describe('ID of the embeddable item to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  itemType: z.enum(['BOOKING', 'EVENT', 'PRODUCT']),
});
export const GetEmbeddableItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Embeddable item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    title: z
      .string()
      .describe('Display title of the embeddable item.')
      .max(300)
      .optional(),
    image: z
      .string()
      .describe('Image associated with the embeddable item.')
      .optional(),
    pageUrl: z
      .string()
      .describe('Universal page URL for the embeddable item.')
      .url()
      .optional(),
    pricingData: z
      .object({
        minValue: z
          .string()
          .describe('Minimum price value. For example, `10.99`.')
          .optional()
          .nullable(),
        maxValue: z
          .string()
          .describe('Maximum price value. For example, `19.99`.')
          .optional()
          .nullable(),
        discountedValue: z
          .string()
          .describe(
            'Price value after discount application. For example, `15.99`. Available only during active promotions.'
          )
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Three-letter currency code in @ISO-4217 alphabetic format.'
          )
          .optional()
          .nullable(),
        pricingPlanId: z
          .string()
          .describe('Pricing plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
      .describe(
        'Pricing information for the embeddable item. Fields are populated based on business logic - single services show `minValue` only, variable pricing shows ranges, and `discountedValue` only appears during active promotions.'
      )
      .optional(),
    itemType: z
      .enum(['BOOKING', 'EVENT', 'PRODUCT'])
      .describe('Type of the app content')
      .optional(),
  }),
  z.xor([
    z.object({
      bookingDetails: z.never().optional(),
      eventDetails: z.never().optional(),
    }),
    z.object({
      eventDetails: z.never().optional(),
      bookingDetails: z
        .object({ durations: z.array(z.number().int()).max(50).optional() })
        .describe(
          'Booking-specific details. Available only when `itemType` is `BOOKING`.'
        ),
    }),
    z.object({
      bookingDetails: z.never().optional(),
      eventDetails: z
        .object({
          formattedDateAndTime: z
            .string()
            .describe(
              'Human-readable date and time representation of the event.'
            )
            .max(500)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe('Date and time when the event starts.')
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe('Date and time when the event ends.')
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe('Event time zone ID in @IANA TZ database format.')
            .max(100)
            .optional()
            .nullable(),
          location: z
            .string()
            .describe('Event location name.')
            .max(50)
            .optional()
            .nullable(),
        })
        .describe(
          'Event-specific details. Available only when `itemType` is `EVENT`.'
        ),
    }),
  ])
);
export const ListEmbeddableItemsRequest = z.object({
  itemType: z.enum(['BOOKING', 'EVENT', 'PRODUCT']),
  options: z
    .object({
      query: z
        .string()
        .describe('Search query to filter items by title.')
        .max(300)
        .optional()
        .nullable(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load per page.\n\nDefault: `50`')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              'Cursor token for pagination. Use the cursor from the previous response to get the next page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor-based pagination parameters.')
        .optional(),
    })
    .optional(),
});
export const ListEmbeddableItemsResponse = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Embeddable item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Display title of the embeddable item.')
            .max(300)
            .optional(),
          image: z
            .string()
            .describe('Image associated with the embeddable item.')
            .optional(),
          pageUrl: z
            .string()
            .describe('Universal page URL for the embeddable item.')
            .url()
            .optional(),
          pricingData: z
            .object({
              minValue: z
                .string()
                .describe('Minimum price value. For example, `10.99`.')
                .optional()
                .nullable(),
              maxValue: z
                .string()
                .describe('Maximum price value. For example, `19.99`.')
                .optional()
                .nullable(),
              discountedValue: z
                .string()
                .describe(
                  'Price value after discount application. For example, `15.99`. Available only during active promotions.'
                )
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Three-letter currency code in @ISO-4217 alphabetic format.'
                )
                .optional()
                .nullable(),
              pricingPlanId: z
                .string()
                .describe('Pricing plan ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Pricing information for the embeddable item. Fields are populated based on business logic - single services show `minValue` only, variable pricing shows ranges, and `discountedValue` only appears during active promotions.'
            )
            .optional(),
          itemType: z
            .enum(['BOOKING', 'EVENT', 'PRODUCT'])
            .describe('Type of the app content')
            .optional(),
        }),
        z.xor([
          z.object({
            bookingDetails: z.never().optional(),
            eventDetails: z.never().optional(),
          }),
          z.object({
            eventDetails: z.never().optional(),
            bookingDetails: z
              .object({
                durations: z.array(z.number().int()).max(50).optional(),
              })
              .describe(
                'Booking-specific details. Available only when `itemType` is `BOOKING`.'
              ),
          }),
          z.object({
            bookingDetails: z.never().optional(),
            eventDetails: z
              .object({
                formattedDateAndTime: z
                  .string()
                  .describe(
                    'Human-readable date and time representation of the event.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                startDate: z
                  .date()
                  .describe('Date and time when the event starts.')
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe('Date and time when the event ends.')
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe('Event time zone ID in @IANA TZ database format.')
                  .max(100)
                  .optional()
                  .nullable(),
                location: z
                  .string()
                  .describe('Event location name.')
                  .max(50)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event-specific details. Available only when `itemType` is `EVENT`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the current response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor for retrieving the next page of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe('Cursor for retrieving the previous page of results.')
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Navigation cursors for moving between result pages.')
        .optional(),
    })
    .describe('Pagination metadata for navigating through results.')
    .optional(),
});
